/**
 * IncidentProximityAlert — Waze-style floating banner that appears when
 * the user drives within ~500m of a reported incident.
 * Shows "Still there?" with Yes / No voting buttons.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { ThumbsUp, ThumbsDown, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const INCIDENT_LABELS = {
  fire: { emoji: '\uD83D\uDD25', label: 'Fire' },
  flood: { emoji: '\uD83C\uDF0A', label: 'Flood' },
  power_outage: { emoji: '\u26A1', label: 'Power Outage' },
  road_accident: { emoji: '\uD83D\uDE97', label: 'Accident' },
  violence: { emoji: '\uD83D\uDEE1\uFE0F', label: 'Violence' },
  medical: { emoji: '\u2764\uFE0F', label: 'Medical' },
  other: { emoji: '\u26A0\uFE0F', label: 'Incident' },
};

const PROXIMITY_RADIUS = 150; // metres — tight radius, only alerts when very close

function haversineDist(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export const IncidentProximityAlert = ({ incidents, onRefresh }) => {
  const [activeAlert, setActiveAlert] = useState(null);
  const [voting, setVoting] = useState(false);
  const dismissedRef = useRef(new Set()); // incident IDs the user already dismissed or voted on
  const lastCheckRef = useRef(0);

  const checkProximity = useCallback((lat, lng) => {
    if (!incidents?.length) return;
    const now = Date.now();
    // Only check every 5 seconds to avoid spam
    if (now - lastCheckRef.current < 5000) return;
    lastCheckRef.current = now;

    for (const inc of incidents) {
      if (dismissedRef.current.has(inc.incident_id)) continue;
      if (!Number.isFinite(inc.lat) || !Number.isFinite(inc.lng)) continue;
      const dist = haversineDist(lat, lng, inc.lat, inc.lng);
      if (dist <= PROXIMITY_RADIUS) {
        setActiveAlert({ ...inc, distance: Math.round(dist) });
        return;
      }
    }
  }, [incidents]);

  // Listen for GPS updates — only alert when actually moving (speed > 2 m/s)
  useEffect(() => {
    const handler = (e) => {
      const { lat, lng, speed } = e.detail || {};
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const speedMps = (speed && Number.isFinite(speed)) ? speed : 0;
      if (speedMps < 2) return; // Don't alert when stationary/planning
      checkProximity(lat, lng);
    };
    window.addEventListener('crisis-gps', handler);
    return () => window.removeEventListener('crisis-gps', handler);
  }, [checkProximity]);

  // Also listen for popup vote events (from the map marker popup)
  useEffect(() => {
    const handler = async (e) => {
      const { id, vote } = e.detail || {};
      if (!id || !vote) return;
      try {
        const endpoint = vote === 'yes' ? 'upvote' : 'downvote';
        const res = await axios.post(`${API}/api/crisis/incidents/${id}/${endpoint}`);
        if (res.data.auto_resolved) {
          toast.success('Incident cleared by community votes');
        } else {
          toast.success(vote === 'yes' ? 'Confirmed — thanks!' : 'Noted — thanks for the update!');
        }
        dismissedRef.current.add(id);
        if (activeAlert?.incident_id === id) setActiveAlert(null);
        if (onRefresh) onRefresh();
      } catch {
        toast.error('Could not submit vote');
      }
    };
    window.addEventListener('incident-vote', handler);
    return () => window.removeEventListener('incident-vote', handler);
  }, [activeAlert, onRefresh]);

  const handleVote = async (vote) => {
    if (!activeAlert || voting) return;
    setVoting(true);
    try {
      const endpoint = vote === 'yes' ? 'upvote' : 'downvote';
      const res = await axios.post(`${API}/api/crisis/incidents/${activeAlert.incident_id}/${endpoint}`);
      if (res.data.auto_resolved) {
        toast.success('Incident cleared by community votes');
      } else {
        toast.success(vote === 'yes' ? 'Confirmed — thanks!' : 'Noted — thanks for the update!');
      }
      dismissedRef.current.add(activeAlert.incident_id);
      setActiveAlert(null);
      if (onRefresh) onRefresh();
    } catch {
      toast.error('Could not submit vote');
    } finally {
      setVoting(false);
    }
  };

  const handleDismiss = () => {
    if (activeAlert) dismissedRef.current.add(activeAlert.incident_id);
    setActiveAlert(null);
  };

  if (!activeAlert) return null;

  const info = INCIDENT_LABELS[activeAlert.type] || INCIDENT_LABELS.other;
  const ups = activeAlert.upvotes || 0;
  const downs = activeAlert.downvotes || 0;

  return (
    <div
      className="absolute top-16 left-3 right-3 z-[700] animate-in slide-in-from-top duration-300"
      data-testid="incident-proximity-alert"
    >
      <div className="bg-zinc-900/95 backdrop-blur-md border border-zinc-700/60 rounded-2xl shadow-2xl px-4 py-3">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center text-lg flex-shrink-0">
            {info.emoji}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-white" data-testid="proximity-alert-title">
                {info.label} ahead — {activeAlert.distance}m
              </p>
              <button onClick={handleDismiss} className="text-zinc-500 hover:text-white ml-2 flex-shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>
            {activeAlert.description && (
              <p className="text-xs text-zinc-400 mt-0.5 truncate">{activeAlert.description}</p>
            )}
            <p className="text-[10px] text-zinc-500 mt-1">
              {ups} confirmed &middot; {downs} say gone &middot; {activeAlert.reported_by_name || 'Anonymous'}
            </p>

            {/* Vote buttons */}
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => handleVote('yes')}
                disabled={voting}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-green-600 hover:bg-green-500 text-white text-xs font-semibold transition-all disabled:opacity-50"
                data-testid="proximity-vote-yes"
              >
                <ThumbsUp className="w-3.5 h-3.5" />
                Still there
              </button>
              <button
                onClick={() => handleVote('no')}
                disabled={voting}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-red-600 hover:bg-red-500 text-white text-xs font-semibold transition-all disabled:opacity-50"
                data-testid="proximity-vote-no"
              >
                <ThumbsDown className="w-3.5 h-3.5" />
                Not anymore
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
